import React, { useEffect, useState } from 'react';
import { useBoolean } from 'react-hanger';
import { useQuery } from '@apollo/client/react';
import { SelectDescriptor } from '@progress/kendo-react-data-tools';
import { GridHeaderSelectionChangeEvent, GridSelectionChangeEvent } from '@progress/kendo-react-grid';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import useGridCustomCell, { INamedItem } from '@components-clean/core/grid/custom-cell';
import Grid from '@components-clean/core/grid-fe';
import { GridColumnProps } from '@components-clean/core/grid-fe/grid.interface';
import { InfoField } from '@components-clean/core/info-field';
import { useUnsavedChangesContext } from '@contexts-clean/unsaved-changes-context';
import style from '@pages-clean/central-monitoring/monitoring-toolkit/tabs/dialogs/style.module.scss';
import { IExtendedLibraryRiskFields } from '@pages-clean/risk-assessment/critical-to-quality-factors/critical-to-quality-factors.interface';
import {
    ILibraryRiskFieldsSubset,
    ISelectedRisks,
} from '@pages-clean/risk-assessment/critical-to-quality-factors/dialogs/assign-risks-to-ctqf-dialog';
import { getFilteredRisks } from '@pages-clean/risk-assessment/helpers';
import { NamedItemType } from 'types/graphql-global-types';
import { useTranslation } from '@hooks/index';
import Constants from '@utils/constants';
import { RiskService } from '@utils/risk.service';
import { notEmpty } from '@utils/utility-helpers';
import { GET_NAMED_ITEMS_BY_TYPE } from '@queries/common';
import { getNamedItemsByType } from '@queries/generated/common.generated';
import { StudyFields } from '@queries/generated/fragments.generated';

interface AddRisksToStudyProps {
    onClose: () => void;
    onSubmit: (libraryRisks: ILibraryRiskFieldsSubset[], close: boolean) => void;
    onOpenAddRisk: () => void;
    study: StudyFields;
    validLibraryRisks: IExtendedLibraryRiskFields[];
    upsertingRisks: boolean;
}

const COMPONENT_NAME = 'add-risks-to-study-dialog';

const AddRisksToStudyDialog = ({
    onClose,
    onSubmit,
    onOpenAddRisk,
    study,
    validLibraryRisks,
    upsertingRisks,
}: AddRisksToStudyProps) => {
    const { confirmDiscardChanges, anyComponentHasChanges, discardChanges, setComponentHasChanges } =
        useUnsavedChangesContext();

    const { t } = useTranslation('ram', { keyPrefix: 'riskLog.dialogs.addRisksToStudy' });

    const { TooltipCell, ActiveInactiveCell } = useGridCustomCell();
    const [filteredRisks, setFilteredRisks] = useState<IExtendedLibraryRiskFields[] | null>(null);
    const clearSelectedRowsEvent = useBoolean(false);
    const [select, setSelect] = React.useState<SelectDescriptor>({});

    const { data: riskCategoryData } = useQuery<getNamedItemsByType>(GET_NAMED_ITEMS_BY_TYPE, {
        variables: { type: NamedItemType.RISK_CATEGORY },
    });

    const statuses: { id: string; text: string }[] = Object.assign([], RiskService.riskStatusList);

    const onSubmitAdd = (close: boolean) => {
        const selectedRows = Object.keys(select).filter((k) => select[k] === true);
        const risks: ISelectedRisks[] =
            filteredRisks
                ?.filter((r) => selectedRows.indexOf(r.id) !== -1)
                .map((risk) => ({
                    id: risk.id,
                    library: risk.library,
                })) ?? [];

        const libraryRiskIds = risks.filter((r) => r.library).map((r) => r.id);

        const selectedLibraryRisks: ILibraryRiskFieldsSubset[] =
            (filteredRisks &&
                filteredRisks
                    .filter((item) => libraryRiskIds.indexOf(item.id) !== -1)
                    .map((risk) => ({
                        responsibleFunctionId: risk.responsibleFunctionId,
                        reviewFrequencyPeriod: risk.reviewFrequencyPeriod,
                        reviewFrequencyUnits: risk.reviewFrequencyUnits,
                        reviewRequired: risk.reviewRequired,
                        controlStrategyId: risk.controlStrategy?.active
                            ? risk.controlStrategy.id
                            : Constants.ObjectId.Empty,
                        statement: risk.statement,
                        libraryReferenceId: risk.id,
                        categoryId: risk.riskCategory?.active ? risk.riskCategory.id : Constants.ObjectId.Empty,
                        studyId: study?.id,
                        status: statuses.find((status) => status.id === 'DRAFT')?.id,
                        vendorIds: risk.vendorIds,
                        countries:
                            risk.countries?.filter(notEmpty).map((country) => ({
                                code: country?.code,
                                name: country?.name,
                            })) ?? [],
                    }))) ??
            [];
        setSelect({});
        clearSelectedRowsEvent.setTrue();
        onSubmit(selectedLibraryRisks, close);
    };

    useEffect(() => {
        if (validLibraryRisks) {
            const filteredValidLibraryRisks = getFilteredRisks(validLibraryRisks);
            setFilteredRisks(filteredValidLibraryRisks.filteredRisks);
        }
    }, [validLibraryRisks]);

    const onCloseHandler = async (event?: unknown) => {
        if (anyComponentHasChanges()) {
            const confirmed = await confirmDiscardChanges();
            if (!confirmed) {
                return;
            }
            discardChanges();
        }

        const action = (event as { action?: string })?.action;
        if (action === 'button') {
            onOpenAddRisk();
        } else {
            onClose();
        }
    };

    const onSelectionChange = React.useCallback((event: GridSelectionChangeEvent) => {
        clearSelectedRowsEvent.setFalse();
        if (event.select) setSelect(event.select);
    }, []);

    const onHeaderSelectionChange = React.useCallback((event: GridHeaderSelectionChangeEvent) => {
        clearSelectedRowsEvent.setFalse();
        if (event.select) setSelect(event.select);
    }, []);

    const columns: GridColumnProps[] = [
        {
            id: 'checkbox',
            field: 'checkbox',
            columnType: 'checkbox',
            className: 'checkboxSelection',
            width: 40,
            customColumn: true,
            resizable: false,
            locked: true,
            filterable: false,
        },
        {
            id: 'category',
            field: 'riskCategory',
            title: t('columns.category'),
            minWidth: 160,
            customColumn: true,
            filter: 'text',
            filterKey: 'name',
            lovData: riskCategoryData?.namedItemsByType
                ? riskCategoryData.namedItemsByType.map((item) => item?.name as string)
                : [],
            cells: {
                data: ActiveInactiveCell({
                    inactive: (dataItem: INamedItem) => dataItem?.active === false,
                    renderValue: (dataItem: INamedItem) => dataItem?.name,
                }),
            },
        },
        {
            id: 'statement',
            field: 'statement',
            title: t('columns.statement'),
            minWidth: 410,
            customColumn: true,
            filter: 'text',
            cells: {
                data: TooltipCell({
                    isCsv: false,
                    maxLines: 2,
                }),
            },
        },
        {
            width: 1,
            minWidth: 1,
            id: 'id',
            field: 'id',
            showByDefault: false,
            title: 'ID',
        },
    ];

    const dialogTitle = `${t('title')}`;
    const formActionsEnabled = Object.keys(select).filter((k) => select[k] === true).length > 0 && !upsertingRisks;

    useEffect(() => {
        setComponentHasChanges(COMPONENT_NAME, formActionsEnabled);
    }, [formActionsEnabled]);

    return (
        <DraggableDialog
            className={style.kWindowContent}
            dataTestId="add-risks-to-study-dialog"
            title={dialogTitle}
            onClose={onCloseHandler}
            onSubmit={() => onSubmitAdd(false)}
            primaryBtnLabel={t('buttons.add')}
            primaryBtnDisabled={!formActionsEnabled}
            secondaryBtnLabel={t('buttons.new')}
            secondaryBtnDisabled={formActionsEnabled}
            onAdditionalAction={() => onSubmitAdd(true)}
            additionalBtnLabel={t('buttons.addAndClose')}
            additionalBtnDisabled={!formActionsEnabled}
            initialHeight={520}
            initialWidth={646}
            height={null}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }} className="k-w-full k-h-full">
                <div>
                    <InfoField type="Info" message={t('infoMessage')} />
                </div>
                <div style={{ height: '300px', paddingBottom: '0.25rem' }}>
                    <Grid
                        excludeHiddenRowMenuActions
                        title={t('title')}
                        maxHeight="auto"
                        testDataId="add-risks-to-study-grid"
                        data={filteredRisks}
                        columns={columns}
                        filterable
                        loading={upsertingRisks}
                        useLoadingOverlay
                        showAddIcon={false}
                        scrollable="virtual"
                        dataItemKey="id"
                        selectable={{
                            enabled: true,
                            drag: false,
                            cell: false,
                            mode: 'multiple',
                        }}
                        onHeaderSelectionChange={onHeaderSelectionChange}
                        onSelectionChange={onSelectionChange}
                        clearGridSelectedRows={clearSelectedRowsEvent.value}
                        useCardWrapper={false}
                        onFilterChange={async () => {
                            clearSelectedRowsEvent.setTrue();
                            setSelect({});
                        }}
                    />
                </div>
            </div>
        </DraggableDialog>
    );
};

export default AddRisksToStudyDialog;
